import React, { useState, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, CustomSelect, Label, ConfirmModal, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { createPayment, updatePayment } from '../services/api';
import { usePaymentStore } from '../stores/pmPaymentStore';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { PMPackage, PMPayment } from '../types/pm';
import { notifyDataChanged } from '../utils/dataEvents';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface PaymentDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  payment?: PMPayment;
  milestones?: Array<{ id: string; name: string; packageId?: string | null }>;
  packages?: Array<Pick<PMPackage, 'id' | 'name'> & { status?: string }>;
  /** 新增時預先帶入的里程碑、分包與金額（應收帳款頁「登記收款」） */
  defaults?: { milestoneId?: string; packageId?: string; amount?: number };
}

interface FormData {
  milestoneId?: string;
  packageId?: string;
  amount: number | '';
  adjustmentAmount: number | '';
  adjustmentType: string;
  paymentMethod: string;
  paymentDate: string;
  invoiceNumber?: string;
  receiptUrl?: string;
  notes?: string;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  payment,
  milestones = [],
  packages = [],
  defaults,
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation(['translation', 'pages']);
  const amountInput = useNumberInput('integer');
  const adjustmentInput = useNumberInput('integer');

  const isEditMode = !!payment;

  const actualHandleClose = () => { setSubmitError(null); reset(); onClose(false); };
  const { handleClose: handleCloseWithWarning, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  // 監聽表單值變化
  const watchedPaymentMethod = watch('paymentMethod', 'BANK_TRANSFER');
  const watchedMilestoneId = watch('milestoneId', '');
  const watchedPackageId = watch('packageId', '');
  const watchedAdjustmentType = watch('adjustmentType', '');
  const watchedAmount = Number(watch('amount')) || 0;
  const watchedAdjustment = Number(watch('adjustmentAmount')) || 0;
  // 從應收帳款登記收款、實收比應收少（客戶代扣所得稅、扣手續費）時，提供一鍵把差額列為收款差額
  const difference = !isEditMode && defaults?.amount && watchedAmount > 0 && !watchedAdjustment ? defaults.amount - watchedAmount : 0;

  // 選了有分包的里程碑時，付款自動跟隨該里程碑的分包
  const milestonePackageId = milestones.find(m => m.id === watchedMilestoneId)?.packageId || '';

  const handleMilestoneChange = (value: string) => {
    setValue('milestoneId', value, { shouldDirty: true });
    const pkgId = milestones.find(m => m.id === value)?.packageId;
    if (pkgId) setValue('packageId', pkgId, { shouldDirty: true });
  };

  // 新增時預設歸入最後一個未完成的分包
  const defaultPackageId = [...packages].reverse().find(p => p.status !== 'COMPLETED' && p.status !== 'CANCELLED')?.id
    || packages[packages.length - 1]?.id
    || '';

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      const today = getTodayDateInput();
      if (payment) {
        // 編輯模式：使用 reset 設置初始值，避免標記為 dirty
        reset({
          milestoneId: payment.milestoneId || '',
          packageId: payment.packageId || '',
          amount: payment.amount,
          adjustmentAmount: payment.adjustmentAmount || '',
          adjustmentType: payment.adjustmentType || '',
          paymentMethod: payment.paymentMethod,
          paymentDate: payment.paymentDate ? toDateInput(payment.paymentDate) : '',
          invoiceNumber: payment.invoiceNumber || '',
          receiptUrl: payment.receiptUrl || '',
          notes: payment.notes || '',
        });
      } else {
        // 新增模式：使用 reset 設置預設值
        reset({
          milestoneId: defaults?.milestoneId || '',
          packageId: defaults?.packageId || defaultPackageId,
          amount: defaults?.amount ?? '',
          adjustmentAmount: '',
          adjustmentType: '',
          paymentMethod: 'BANK_TRANSFER',
          paymentDate: today,
          invoiceNumber: '',
          receiptUrl: '',
          notes: '',
        });
      }
    }
    // 只在開啟、換編輯對象時帶入；不依賴分包清單（分包重新載入時會清掉使用者輸入到一半的內容）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, payment, reset]);

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    setLoading(true);

    try {
      const payload = {
        milestoneId: data.milestoneId || null,
        packageId: (milestonePackageId || data.packageId) || null,
        amount: parseFloat(data.amount.toString()),
        adjustmentAmount: Number(data.adjustmentAmount) || 0,
        adjustmentType: Number(data.adjustmentAmount) > 0 ? data.adjustmentType || null : null,
        paymentMethod: data.paymentMethod,
        paymentDate: data.paymentDate ? toISOString(data.paymentDate) : null,
        invoiceNumber: data.invoiceNumber?.trim() || null,
        receiptUrl: data.receiptUrl?.trim() || null,
        notes: data.notes?.trim() || null,
      };

      const response = isEditMode
        ? await updatePayment(payment.id, payload)
        : await createPayment(projectId, payload);

      toast.success(
        isEditMode
          ? t('common.messages.updateSuccess')
          : t('common.messages.createSuccess')
      );

      // CRM 模式：立即更新 store
      if (isEditMode && response?.data) {
        usePaymentStore.getState().update(payment.id, response.data);
      } else if (response?.data) {
        usePaymentStore.getState().add(response.data);
      }

      // 分包已收款、專案已收／待收款由訂閱者重抓（專案詳情、專案列表）
      notifyDataChanged('payments');

      actualHandleClose();
    } catch (error: any) {
      console.error('Failed to save payment:', error);
      setSubmitError(error.message || 'Failed to save payment');
      toast.error(
        isEditMode
          ? t('pmPayments.updateError', { ns: 'pages' }) || 'Failed to update payment'
          : t('pmPayments.createError', { ns: 'pages' }) || 'Failed to create payment'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} closeDisabled={loading} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmPayments.editTitle', { ns: 'pages' }) || 'Edit Payment' : t('pmPayments.addTitle', { ns: 'pages' }) || 'Add Payment'} onClose={handleCloseWithWarning} closeDisabled={loading} closeLabel={t('common.actions.close')} />

        {/* 對話框內容 */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {/* 付款金額 + 付款方式 */}
            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmPayments.fields.amount', { ns: 'pages' }) || 'Amount'}
                required={true}
                error={errors.amount?.message}
                htmlFor="amount"
              >
                <Input
                  type="text"
                  id="amount"
                  {...amountInput.bind(register('amount', {
                    required: t('pmPayments.amountRequired', { ns: 'pages' }) || 'Amount is required',
                    ...amountInput.validationRules
                  }))}
                  variant={errors.amount ? "error" : "default"}
                  placeholder="0"
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmPayments.fields.paymentMethod', { ns: 'pages' }) || 'Payment Method'}
                required={true}
                htmlFor="paymentMethod"
              >
                <CustomSelect
                  id="paymentMethod"
                  {...register('paymentMethod', { required: t('common.forms.validation.required') })}
                  value={watchedPaymentMethod}
                  onChange={(value) => setValue('paymentMethod', value)}
                  options={[
                    { value: 'BANK_TRANSFER', label: t('pmPayments.paymentMethod.BANK_TRANSFER', { ns: 'pages' }) || 'Bank Transfer' },
                    { value: 'CASH', label: t('pmPayments.paymentMethod.CASH', { ns: 'pages' }) || 'Cash' },
                    { value: 'CHECK', label: t('pmPayments.paymentMethod.CHECK', { ns: 'pages' }) || 'Check' },
                    { value: 'CREDIT_CARD', label: t('pmPayments.paymentMethod.CREDIT_CARD', { ns: 'pages' }) || 'Credit Card' },
                    { value: 'OTHER', label: t('pmPayments.paymentMethod.OTHER', { ns: 'pages' }) || 'Other' },
                  ]}
                />
              </FormFieldWithError>
            </div>

            {/* 收款差額 + 類型：客戶代扣所得稅、匯款手續費、折讓 */}
            <div>
              <div className="grid grid-cols-2 gap-4">
                <FormFieldWithError
                  label={t('pmPayments.fields.adjustmentAmount', { ns: 'pages' })}
                  error={errors.adjustmentAmount?.message}
                  htmlFor="adjustmentAmount"
                >
                  <Input
                    type="text"
                    id="adjustmentAmount"
                    {...adjustmentInput.bind(register('adjustmentAmount', adjustmentInput.validationRules))}
                    variant={errors.adjustmentAmount ? 'error' : 'default'}
                    placeholder="0"
                  />
                </FormFieldWithError>
                <FormFieldWithError
                  label={t('pmPayments.fields.adjustmentType', { ns: 'pages' })}
                  required={watchedAdjustment > 0}
                  error={errors.adjustmentType?.message}
                  htmlFor="adjustmentType"
                >
                  <CustomSelect
                    id="adjustmentType"
                    {...register('adjustmentType', {
                      validate: value => !(Number(watch('adjustmentAmount')) > 0) || !!value || t('pmPayments.adjustmentTypeRequired', { ns: 'pages' }),
                    })}
                    value={watchedAdjustmentType}
                    onChange={(value) => setValue('adjustmentType', value, { shouldDirty: true, shouldValidate: !!errors.adjustmentType })}
                    placeholder="—"
                    options={(['WITHHOLDING', 'BANK_FEE', 'DISCOUNT'] as const).map(type => ({
                      value: type,
                      label: t(`pmPayments.adjustmentType.${type}`, { ns: 'pages' }),
                    }))}
                  />
                </FormFieldWithError>
              </div>
              {difference > 0 ? (
                <button
                  type="button"
                  onClick={() => setValue('adjustmentAmount', difference, { shouldDirty: true })}
                  className="mt-1 text-[13px] text-accent hover:underline"
                >
                  {t('pmPayments.fillDifference', { ns: 'pages', amount: difference.toLocaleString() })}
                </button>
              ) : (
                <p className="mt-1 text-[13px] text-fg-secondary">{t('pmPayments.adjustmentHint', { ns: 'pages' })}</p>
              )}
            </div>

            {/* 付款日期 + 發票號碼 */}
            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmPayments.fields.paymentDate', { ns: 'pages' }) || 'Payment Date'}
                required={true}
                error={errors.paymentDate?.message}
                htmlFor="paymentDate"
              >
                <Input
                  type="date"
                  id="paymentDate"
                  {...register('paymentDate', {
                    required: t('pmPayments.paymentDateRequired', { ns: 'pages' }) || 'Payment date is required'
                  })}
                  variant={errors.paymentDate ? "error" : "default"}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmPayments.fields.invoiceNumber', { ns: 'pages' }) || 'Invoice Number'}
                error={errors.invoiceNumber?.message}
                htmlFor="invoiceNumber"
              >
                <Input
                  type="text"
                  id="invoiceNumber"
                  {...register('invoiceNumber', {
                    maxLength: { value: 100, message: t('pmPayments.validation.invoiceNumberMaxLength', { ns: 'pages' }) }
                  })}
                />
              </FormFieldWithError>
            </div>

            {/* 關聯里程碑 */}
            <div>
              <Label htmlFor="milestoneId">{t('pmPayments.fields.milestone', { ns: 'pages' }) || 'Milestone'}</Label>
              <CustomSelect
                id="milestoneId"
                {...register('milestoneId')}
                value={watchedMilestoneId}
                onChange={handleMilestoneChange}
                placeholder={t('pmPayments.selectMilestone', { ns: 'pages' }) || 'Select milestone (optional)'}
                options={[
                  { value: '', label: t('pmPayments.noMilestone', { ns: 'pages' }) || 'No milestone' },
                  ...milestones.map(m => ({ value: m.id, label: m.name }))
                ]}
              />
            </div>

            {/* 所屬分包 */}
            {packages.length > 0 && (
              <div>
                <Label htmlFor="packageId">{t('pmPayments.fields.package', { ns: 'pages' }) || 'Package'}</Label>
                <CustomSelect
                  id="packageId"
                  value={milestonePackageId || watchedPackageId || ''}
                  onChange={(value) => setValue('packageId', value, { shouldDirty: true })}
                  disabled={!!milestonePackageId}
                  options={[
                    { value: '', label: t('pmPayments.noPackage', { ns: 'pages' }) || 'No package' },
                    ...packages.map(p => ({ value: p.id, label: p.name })),
                  ]}
                />
                {milestonePackageId && (
                  <p className="mt-1 text-[13px] text-fg-secondary">
                    {t('pmPayments.packageFromMilestone', { ns: 'pages' }) || 'Follows the selected milestone'}
                  </p>
                )}
              </div>
            )}

            {/* 收據/發票附件 URL */}
            <FormFieldWithError
              label={t('pmPayments.fields.receiptUrl', { ns: 'pages' }) || '收據/發票附件 URL'}
              error={errors.receiptUrl?.message}
              htmlFor="receiptUrl"
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PaperClipIcon className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="url"
                  id="receiptUrl"
                  {...register('receiptUrl', {
                    maxLength: { value: 2000, message: t('common.forms.validation.maxLength', { length: 2000 }) }
                  })}
                  className="pl-9"
                  placeholder="https://..."
                />
              </div>
              {watch('receiptUrl') && (
                <a
                  href={watch('receiptUrl')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] text-accent hover:underline mt-1 inline-block"
                >
                  {t('pmPayments.viewReceipt', { ns: 'pages' }) || '開啟附件'}
                </a>
              )}
            </FormFieldWithError>

            {/* 備註 */}
            <FormFieldWithError
              label={t('pmPayments.fields.notes', { ns: 'pages' }) || 'Notes'}
              error={errors.notes?.message}
              htmlFor="notes"
            >
              <Textarea
                id="notes"
                {...register('notes', {
                  maxLength: { value: 1000, message: t('pmPayments.validation.notesMaxLength', { ns: 'pages' }) }
                })}
                rows={3}
                variant={errors.notes ? "error" : "default"}
              />
            </FormFieldWithError>

            {submitError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
                {submitError}
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
              disabled={loading}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              isEditMode={isEditMode}
              customText={{ create: t('common.actions.create'), createLoading: t('common.status.creating') }}
              t={t}
            />
          </DialogFooter>
        </form>
    </DialogShell>

      <ConfirmModal isOpen={showConfirm} onClose={handleCancelClose} onConfirm={handleConfirmClose} title={t('components.modal.unsavedChanges.title')} message={t('components.modal.unsavedChanges.message')} confirmText={t('components.modal.unsavedChanges.confirmButton')} cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t} />
    </>
  );
};

export default PaymentDialog;
